// The <title> and meta description of every route under the marketing layout.
//
// They live in route meta (`meta.title`, `meta.description`) so one place sets
// them for the browser and for the build-time prerender: layouts/Marketing.vue
// reads them through resolveMarketingMeta and applies them with Quasar's
// useMeta, which writes into the server-rendered HTML. The router's afterEach
// title hook cannot do this (it needs `document`, and on the client it would
// overwrite the title the server rendered), so it skips these routes.
//
// A value is a string, or a function of the route for pages whose text depends
// on a param (the program pages).

export const HOME_TITLE = "ChartFlow - The Most Powerful EHR for Education & Simulation";
export const HOME_DESCRIPTION =
  "ChartFlow is an academic EHR for nursing, simulation, medical assisting, allied health, and CTE programs. Create a free instructor demo account today.";

import { siteOrigin } from "../utils/domains";

const resolveValue = (value, route) => (typeof value === "function" ? value(route) : value);

// { title, description } for a route, each a string or null (unknown program slug).
export function resolveMarketingMeta(route) {
  const meta = route.meta || {};
  return {
    title: resolveValue(meta.title, route) ?? null,
    description: resolveValue(meta.description, route) ?? null,
  };
}

// The path a page's canonical URL carries: the route's path without query or
// hash, and without a trailing slash (`/` stays `/`, and is joined to the origin
// as nothing at all, so the homepage's canonical is the bare origin).
export function canonicalPath(route) {
  const path = String(route.path || "/").split(/[?#]/)[0].replace(/\/+$/, "");
  return path || "/";
}

// The canonical URL of a route on a site origin.
export function canonicalHref(route, origin) {
  const path = canonicalPath(route);
  return path === "/" ? origin : `${origin}${path}`;
}

// The Quasar useMeta object for a route. Fields without a value are left out, so
// a page that has no description keeps the static fallbacks in index.html.
// og:description carries the same text: link unfurlers read it in preference to
// the plain description, and the static one in index.html is the homepage's.
//
// A page that resolves a title also gets a canonical link on the stage's own
// site origin: organization subdomains serve the same HTML, and without it
// `missouri.chartflow.io/features` would be a duplicate of `www`'s. A page with
// no title (the not-found page, an unknown program) gets none. Without a known
// origin (plain test runs) there is none either.
export function marketingMetaFor(route) {
  const { title, description } = resolveMarketingMeta(route);
  const result = {};
  if (title) result.title = title;
  if (description) {
    result.meta = {
      description: { name: "description", content: description },
      ogDescription: { property: "og:description", content: description },
    };
  }
  const origin = siteOrigin();
  if (title && origin) {
    result.link = { canonical: { rel: "canonical", href: canonicalHref(route, origin) } };
  }
  return result;
}
